import React from "react";
import _ from "lodash";
import Grid from "@mui/material/Grid";
import SubmitButton from "../../../Button/SubmitButton";
import caseFullFormStyle from "./caseFullFormStyle";
import { transformToFormValues } from "../../../../services/formServiceFunctions";
import { persist } from "../../../../services/Persist/persisterService";
import useForm, { UseFormProps } from "../../../../hooks/useForm";
import ValidatorForm from "../../../Validator/ValidatorForm";
import ContentBox from "../../../ContentBox/ContentBox";
import { Box, Tab, Tabs, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../../services/ReactQuery/reactQueryService";
import { getProductData } from "../../../../services/Product/ProductService/productService";
import { HashLink as Link } from "react-router-hash-link";
import { SubFormDefinition } from "../../../../types/FormDefinitions/FullFormDefinition";

import { ProductUnion } from "../../../../types/ProductUnion";
import SubFormSection from "./SubFormSection";

type SubFormProps = {
  headline: string;
  formDefinition: SubFormDefinition;
  product: ProductUnion;
  formDisabled?: boolean;
  refreshPage: Function;
};

const SubForm = ({ headline, formDefinition, product, formDisabled = false, refreshPage }: SubFormProps) => {
  const queryClient = useQueryClient();
  const initialValues = {
    [product.productClassName]: transformToFormValues(product),
  };

  const onSubmit: UseFormProps["onSubmit"] = async ({ values }) => {
    await persist(values, product);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.item(getProductData(product.productClassName, "apiUri"), product.id)
    });
  };

  const useFormProps = useForm({
    initialValues,
    onSubmit,
    identifier: product,
  });

  const visibleSections = formDefinition.sections.filter(
    (section) =>
      !section.isHidden ||
      !section.isHidden({
        values: useFormProps.values,
        product,
      })
  );

  const getAnchorId = (formSectionLabel: string) => {
    return encodeURIComponent(`${headline}-${formSectionLabel}`.replaceAll(" ", "-").toLowerCase());
  };

  return (
    <ContentBox headline={headline} headlineVariant="h2">
      {!formDefinition.asAccordion && visibleSections.length > 1 && (
        <Grid
          size={{
            xs: 12,
            lg: 8,
          }}
        >
          <Tabs variant={"fullWidth"} scrollButtons="auto" value={false} centered>
            {visibleSections.map((formSection, index) => {
              if (!formSection.label) {
                return null;
              }
              return (
                <Tab
                  component={Link}
                  to={`#${getAnchorId(formSection.label)}`}
                  label={formSection.label}
                  key={index}
                  wrapped={true}
                />
              );
            })}
          </Tabs>
        </Grid>
      )}
      <ValidatorForm onSubmit={useFormProps.handleSubmit}>
        <Box>
          {_.map(visibleSections, (formSection, index) => {
            return (
              <SubFormSection
                formSection={formSection}
                product={product}
                formDisabled={formDisabled}
                refreshPage={refreshPage}
                useFormProps={useFormProps}
                getAnchorId={getAnchorId}
                asAccordion={formDefinition.asAccordion}
                key={index}
              />
            );
          })}
        </Box>
        {!formDefinition.withoutSubmit && (
          <Grid container sx={caseFullFormStyle.grid}>
            <Grid size={12}>
              <SubmitButton
                error={_.keys(useFormProps.errors).length > 0}
                isLoading={useFormProps.isLoading}
                disabled={_.keys(useFormProps.touchedValues).length === 0}
                variant={"contained"}
                type={"submit"}
              >
                Änderungen Speichern
              </SubmitButton>
            </Grid>
          </Grid>
        )}
      </ValidatorForm>
    </ContentBox>
  );
};

export default SubForm;
